import * as React from "react";
import MetadataViewer from "./MetadataViewer";
import TroubleshootCancelButton from "./TroubleshootCancelButton";
import Step from "./troubleshoot/Step";
import ReportTroubleshoot, { MouthType } from "./troubleshoot/ReportTroubleshoot";
import ScanPackageViewer from "./commons/components/ScanPackageViewer";
import StatusViewer from "./commons/components/StatusViewer";
import UserCardMedium from "./commons/components/user/UserCardMedium";
import ReportStatus from "./commons/model/report/ReportStatus";
import PatientAnswers from "./commons/components/PatientAnswers";
import { createRoot } from "react-dom/client";
import { dateWithOptionalTime } from "./commons/utils/date";

interface TroubleshootProps {
  troubleshoot: ReportTroubleshoot;
}

export default class Troubleshoot extends React.PureComponent<TroubleshootProps> {
  render() {
    return (
      <>
        <div className="header-bar h-flex space-between align-center">
          <div className="header-bar-title">{i18n["Page.Reports.Troubleshoot.Title"]}</div>
          <StatusViewer status={this.props.troubleshoot.statusInfo.status} />
        </div>

        <div className="colored-section group">
          <div className="container">
            {this.props.troubleshoot.analysis && (
              <div className="analysis">
                {i18n["Page.Reports.Troubleshoot.Analysis.Id"]}
                <a className="link" href={this.props.troubleshoot.analysis.href} target="_blank">
                  {this.props.troubleshoot.analysis.uid}
                </a>
              </div>
            )}
            <div className="h-flex">
              <div className="panel">
                <UserCardMedium
                  user={this.props.troubleshoot.patientInfo}
                  pageUrl={this.props.troubleshoot.patientFileUrl}
                />
              </div>
              <div className="panel">
                <UserCardMedium user={this.props.troubleshoot.assignmentInfo} />
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container">
            {this.props.troubleshoot.cancelUrl && (
              <div className="actions">
                <TroubleshootCancelButton cancelEndpoint={this.props.troubleshoot.cancelUrl} />
              </div>
            )}
            {this.props.troubleshoot.patientAnswers.length ? (
              <PatientAnswers answers={this.props.troubleshoot.patientAnswers} />
            ) : null}

            <div className="panel with-shadow">
              <div className="header-status h-flex balanced">
                <Step step={this.props.troubleshoot.processing} />
                <Step step={this.props.troubleshoot.upload} />
                <Step step={this.props.troubleshoot.creation} />
              </div>
              {this.props.troubleshoot.statusInfo.status === ReportStatus.TECHNICAL_ERROR ? (
                <div className="v-flex align-center">
                  <div className="message danger">
                    {i18n["Page.Reports.Troubleshoot.TechnicalError.Title"]}
                  </div>
                  <div className="detail">
                    {i18n["Page.Reports.Troubleshoot.TechnicalError.Detail"]}{" "}
                    {this.props.troubleshoot.technicalErrorMsg}
                  </div>
                </div>
              ) : (
                <>
                  <div className="photos">
                    <div className="subtitle">
                      {i18n["Page.Reports.Troubleshoot.Subtitle.Photos"]}
                    </div>
                    {Object.keys(this.props.troubleshoot.uploadedPhotos).map((mouthType) => (
                      <ScanPackageViewer
                        key={mouthType}
                        mouthType={mouthType as MouthType}
                        photoSet={this.props.troubleshoot.uploadedPhotos[mouthType]}
                        displayAllRejectionCauses={!!this.props.troubleshoot.metadata}
                      />
                    ))}
                  </div>

                  {this.props.troubleshoot.metadata && (
                    <div>
                      <div className="subtitle">
                        {i18n["Page.Reports.Troubleshoot.Subtitle.Metadata"]}
                      </div>
                      <MetadataViewer metadataItems={this.props.troubleshoot.metadata} />
                    </div>
                  )}

                  {this.props.troubleshoot.events && this.renderEventTable()}
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  renderEventTable() {
    return (
      <div>
        <div className="subtitle">{i18n["Page.Reports.Troubleshoot.Subtitle.Events"]}</div>
        <table className="table with-shadow">
          <thead>
            <tr>
              <th>{i18n["Page.Report.Troubleshoot.Events.Event"]}</th>
              <th>{i18n["Page.Report.Troubleshoot.Events.Date"]}</th>
              <th>{i18n["Page.Report.Troubleshoot.Events.Detail"]}</th>
            </tr>
          </thead>
          <tbody>
            {this.props.troubleshoot.events.map((e) => (
              <tr key={`${e.name}—${e.date}`}>
                <td>{e.name}</td>
                <td>{dateWithOptionalTime(e.date)}</td>
                <td>
                  {e.description}
                  {e.uri && <a href={e.uri}>{e.uri}</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  static loadComponent(elementId: string, props: TroubleshootProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<Troubleshoot {...props} />);
  }
}
